import type { Types } from '@amplitude/analytics-browser';
import { useStorage } from '@vueuse/core';
import { ref } from 'vue';
import { ANALYTICS_CLICK_BEHAVIOUR_ATTRIBUTE } from '@pkg/config/misc/analyticsClickBehaviour';
import { CMS_DEPTH_ATTRIBUTE } from '@pkg/config/misc/cmsDepth';
import { logger } from '@pkg/logger';
import {
  getLocale,
  getPageType,
  normalizeElementHrefForAmplitude,
} from '../utilities/amplitudePageContext';

type EnrichmentPlugin = Types.EnrichmentPlugin;
type Event = Types.Event;

const AMPLITUDE_ELEMENT_HIERARCHY = '[Amplitude] Element Hierarchy';
const AMPLITUDE_ELEMENT_HREF = '[Amplitude] Element Href';

/** Index 0 = click target, higher = DOM parents. Innermost `data-cms-depth` + optional ancestor `data-analytics-click-behaviour`. */
function cmsBlockIndexFromAmplitudeHierarchy(hierarchy: unknown): string | undefined {
  const nodes = typeof hierarchy === 'string' ? (JSON.parse(hierarchy) as unknown) : hierarchy;
  if (!Array.isArray(nodes)) { return undefined; }
  let metricsIdx = -1;
  let metrics: string | undefined;
  for (let i = 0; i < nodes.length; i++) {
    const v = (nodes[i] as { attrs?: Record<string, string> })?.attrs?.[CMS_DEPTH_ATTRIBUTE];
    if (v) {
      metrics = String(v);
      metricsIdx = i;
      break;
    }
  }
  if (metrics === undefined) { return undefined; }
  for (let i = metricsIdx + 1; i < nodes.length; i++) {
    const b = (nodes[i] as { attrs?: Record<string, string> })?.attrs?.[ANALYTICS_CLICK_BEHAVIOUR_ATTRIBUTE];
    if (b) {
      return `${metrics}-${String(b)}`;
    }
  }
  return metrics;
}

/** First `data-analytics-click-behaviour` on the click target or its closest ancestor (hierarchy index 0 = target). */
function analyticsClickBehaviourFromAmplitudeHierarchy(hierarchy: unknown): string | undefined {
  const nodes = typeof hierarchy === 'string' ? (JSON.parse(hierarchy) as unknown) : hierarchy;
  if (!Array.isArray(nodes)) { return undefined; }
  for (let i = 0; i < nodes.length; i++) {
    const b = (nodes[i] as { attrs?: Record<string, string> })?.attrs?.[ANALYTICS_CLICK_BEHAVIOUR_ATTRIBUTE];
    if (b) { return String(b); }
  }
  return undefined;
}

export class ExtendAmplitudeEvents implements EnrichmentPlugin {
  name = 'extend-amplitude-page-viewed-event';
  parallel = true;
  type = 'enrichment' as const;

  setup(): Promise<void> {
    return Promise.resolve();
  }

  execute(event: Event): Promise<Event> {
    const props = event.event_properties as Record<string, unknown> | undefined;

    if (event.event_type === '[Amplitude] Page Viewed') {
      const url = event?.event_properties?.['[Amplitude] Page URL'] ? event?.event_properties?.['[Amplitude] Page URL'] : '';
      (event.event_properties as Record<string, unknown>).locale = getLocale(url);
      (event.event_properties as Record<string, unknown>).page_type = getPageType(url);
    }

    let amplitudeForm = ref();
    if (event.event_type === '[Amplitude] Form Submitted') {
      try {
        amplitudeForm = useStorage('amplitudeForm', {});
        const formName = amplitudeForm?.value?.formName || JSON.parse(amplitudeForm.value).formName;
        const formId = amplitudeForm?.value?.formId || JSON.parse(amplitudeForm.value).formId;
        (event.event_properties as Record<string, unknown>)['[Amplitude] Form Name'] = formName;
        (event.event_properties as Record<string, unknown>)['[Amplitude] Form ID'] = formId;

        // Push event to GTM dataLayer
        if (typeof window !== 'undefined' && (window as any).dataLayer) {
          try {
            if (formName === 'STORE_LEAD') {
              const url = event?.event_properties?.['[Amplitude] Form Destination'] ? event?.event_properties?.['[Amplitude] Form Destination'] : '';
              ((window as any).dataLayer as Array<Record<string, unknown>>).push({
                event: 'form.submit',
                form_name: formName,
                form_id: formId,
                domain: getLocale(url),
              });
            }
          } catch (gtmError) {
            logger.error('[amplitude plugin] - Error pushing to GTM dataLayer', gtmError);
          }
        }
      } catch (e) {
        logger.error('[amplitude plugin] - Error setting amplitude form properties', e);
      }
    }

    if (event.event_type === 'form_error') {
      try {
        amplitudeForm = useStorage('amplitudeForm', {});
        (event.event_properties as Record<string, unknown>)['[Amplitude] Form Name'] = amplitudeForm?.value?.formName || JSON.parse(amplitudeForm.value).formName;
        (event.event_properties as Record<string, unknown>)['[Amplitude] Form ID'] = amplitudeForm?.value?.formId || JSON.parse(amplitudeForm.value).formId;
      } catch (e) {
        logger.error('[amplitude plugin] - Error setting amplitude form properties', e);
      }
    }

    if (event.event_type === '[Amplitude] Form Started') {
      try {
        amplitudeForm = useStorage('amplitudeForm', {});
        (event.event_properties as Record<string, unknown>)['[Amplitude] Form Name'] = amplitudeForm?.value?.formName || JSON.parse(amplitudeForm.value).formName;
        (event.event_properties as Record<string, unknown>)['[Amplitude] Form ID'] = amplitudeForm?.value?.formId || JSON.parse(amplitudeForm.value).formId;
      } catch (e) {
        logger.error('[amplitude plugin] - Error setting amplitude form properties', e);
      }
    }

    if (event.event_type === 'single_sign_up' || event.event_type === 'mystore_login') {
      let pageType = 'UNKNOWN-PAGE';
      try {
        pageType = getPageType(window.location.href) ?? pageType;
      } catch (e) {
        logger.error('Error in getPageType: ', e);
      }

      (event.event_properties as Record<string, unknown>) = event.event_properties ?? {};
      (event.event_properties as Record<string, unknown>).page_type = pageType;
    } else if (event.event_type === '[Amplitude] Element Clicked' && props) {
      try {
        const hierarchy = props[AMPLITUDE_ELEMENT_HIERARCHY];
        const rawHref = props[AMPLITUDE_ELEMENT_HREF];

        if (typeof rawHref === 'string' && rawHref.trim() && rawHref !== 'none') {
          props[AMPLITUDE_ELEMENT_HREF] = normalizeElementHrefForAmplitude(rawHref);
        }

        const cmsBlockIndex = cmsBlockIndexFromAmplitudeHierarchy(hierarchy);
        const analyticsClickBehaviour = analyticsClickBehaviourFromAmplitudeHierarchy(hierarchy);
        logger.info('cmsBlockIndex:', cmsBlockIndex, 'nearest data-analytics-click-behaviour:', analyticsClickBehaviour);
        if (cmsBlockIndex !== undefined) {
          props.page_block_depth = cmsBlockIndex;
          props.triggered_behaviour = analyticsClickBehaviour;
        }
      } catch (err) {
        logger.error('[amplitude plugin] - Error enriching Element Clicked', err);
      }
    }

    if (props) {
      delete props[AMPLITUDE_ELEMENT_HIERARCHY];
    }

    return Promise.resolve(event);
  }
};
